import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import countryCodesData from '../../../data/country-codes.json';
import { searchGuestByPhone } from '../../../services/api';
import { normalizePhoneInputForCountry } from '../../../utils/phoneValidation';

function buildGuestDisplayName(guest) {
  return [
    guest?.first_name || '',
    guest?.middle_name || '',
    guest?.last_name || '',
  ].filter(Boolean).join(' ') || guest?.name || 'Guest';
}

export default function HallEventGuestPhoneField({
  phone = '',
  countryCode = '+91',
  onPhoneChange,
  onCountryCodeChange,
  onGuestSelect,
  disabled = false,
  required = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [guestOptions, setGuestOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const selectedCountry = useMemo(
    () => countryCodesData.find((country) => country.dialCode === countryCode)
      || countryCodesData.find((country) => country.code === 'IN'),
    [countryCode]
  );

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return countryCodesData;
    const query = search.trim().toLowerCase();
    return countryCodesData.filter((country) => (
      country.name.toLowerCase().includes(query)
      || country.dialCode.includes(query)
      || country.code.toLowerCase().includes(query)
    ));
  }, [search]);

  const searchGuestDebounced = useCallback(async (phoneNumber) => {
    const digitsOnly = String(phoneNumber || '').replace(/\D/g, '');
    if (digitsOnly.length < 3) {
      setGuestOptions([]);
      return;
    }

    setSearching(true);
    try {
      const response = await searchGuestByPhone(digitsOnly);
      if (response?.guest) {
        const guest = response.guest;
        setGuestOptions([{
          id: guest.id,
          name: buildGuestDisplayName(guest),
          phone: guest.phone || digitsOnly,
          email: guest.email || '',
          guest,
        }]);
      } else {
        setGuestOptions([]);
      }
    } catch (err) {
      setGuestOptions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handlePhoneInputChange = (nextPhone) => {
    const normalizedPhone = normalizePhoneInputForCountry(nextPhone, countryCode);
    onPhoneChange?.(normalizedPhone);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const digitsOnly = normalizedPhone.replace(/\D/g, '');
    if (digitsOnly.length >= 3) {
      debounceRef.current = setTimeout(() => {
        searchGuestDebounced(normalizedPhone);
      }, 400);
    } else {
      setGuestOptions([]);
    }
  };

  const handleGuestPick = (_, value, reason) => {
    if (reason === 'clear' || !value || typeof value === 'string') {
      return;
    }

    const guest = value.guest;
    if (!guest) return;

    const guestName = buildGuestDisplayName(guest);
    const rawPhoneDigits = value.phone ? String(value.phone).replace(/\D/g, '') : '';
    const guestDialCode = guest.country_code ? String(guest.country_code).replace(/\D/g, '') : '';
    const phoneNational = (guestDialCode && rawPhoneDigits.startsWith(guestDialCode))
      ? rawPhoneDigits.slice(guestDialCode.length)
      : rawPhoneDigits;

    if (guest.country_code && onCountryCodeChange) {
      const code = guest.country_code.startsWith('+') ? guest.country_code : `+${guest.country_code}`;
      const validDialCode = countryCodesData.find((country) => country.dialCode === code);
      onCountryCodeChange(validDialCode ? validDialCode.dialCode : '+91');
    }

    onPhoneChange?.(normalizePhoneInputForCountry(phoneNational, guest.country_code || countryCode));
    onGuestSelect?.({
      name: guestName,
      email: guest.email || '',
      guest,
    });
    setGuestOptions([]);
  };

  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  return (
    <>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={guestOptions}
        loading={searching}
        value={null}
        inputValue={phone}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.phone || '')}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input' || reason === 'clear') {
            handlePhoneInputChange(newInputValue);
          }
        }}
        onChange={handleGuestPick}
        filterOptions={(options) => options}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {option.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {option.phone}{option.email ? ` · ${option.email}` : ''}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Phone"
            size="small"
            fullWidth
            required={required}
            sx={{
              '& .MuiFormLabel-asterisk': {
                color: 'inherit',
              },
            }}
            inputProps={{
              ...params.inputProps,
              maxLength: normalizePhoneInputForCountry('9'.repeat(20), countryCode).length,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Box
                      ref={anchorRef}
                      onClick={() => {
                        if (!disabled) setMenuOpen(true);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: disabled ? 'default' : 'pointer',
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        '&:hover': disabled
                          ? {}
                          : {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                          },
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                        {selectedCountry?.flag || '🇮🇳'}
                      </Typography>
                      <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {selectedCountry?.dialCode || '+91'}
                      </Typography>
                      {!disabled ? (
                        <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          ▼
                        </Typography>
                      ) : null}
                    </Box>
                  </InputAdornment>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
          setSearch('');
        }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 300,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search country..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 1 }}
          />
        </Box>
        {filteredCountries.map((country) => (
          <MenuItem
            key={country.code}
            selected={selectedCountry?.dialCode === country.dialCode}
            onClick={() => {
              const normalizedPhone = normalizePhoneInputForCountry(phone, country.dialCode);
              onCountryCodeChange?.(country.dialCode);
              if (normalizedPhone !== phone) {
                onPhoneChange?.(normalizedPhone);
              }
              setMenuOpen(false);
              setSearch('');
            }}
          >
            <ListItemIcon>
              <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
            </ListItemIcon>
            <ListItemText
              primary={country.dialCode}
              secondary={country.name}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
